import React from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { format, parseISO } from 'date-fns';

const LoginCharts = ({ successfulLogins, failedLogins }) => {
    // Process data for charts
    const processChartData = (data) => {
        return data.map(item => ({
            ...item,
            formattedDate: format(parseISO(item.date), 'MMM d'),
            fullDate: item.date
        }));
    };

    const chartData = processChartData(successfulLogins);
    const failedChartData = processChartData(failedLogins);

    // Custom tooltip component
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-bgSecondary shadow-neo-card p-3 rounded border border-bgAccent">
                    <p className="text-font-primary font-medium">{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} className="text-sm" style={{ color: entry.color }}>
                            {entry.name}: {entry.value}
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="flex flex-col md:flex-row gap-3">
            {/* Successful Logins Line Chart */}
            <div className="card-container p-4 rounded-md w-full flex flex-col gap-3">
                <div className="flex items-center gap-1">
                    <Icon icon="mdi:login" className="text-xl text-green-500" />
                    <h4 className="font-semibold text-font-primary">Successful Logins</h4>
                </div>
                
                {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300} className="-ml-8">
                        <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                            <XAxis 
                                dataKey="formattedDate" 
                                stroke="#9CA3AF"
                                fontSize={12}
                            />
                            <YAxis 
                                stroke="#9CA3AF"
                                fontSize={12}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Line 
                                type="monotone" 
                                dataKey="count" 
                                stroke="#10B981" 
                                strokeWidth={2}
                                dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                                activeDot={{ r: 6, stroke: '#10B981', strokeWidth: 2 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="flex items-center justify-center h-64 text-font-secondary">
                        <div className="text-center">
                            <Icon icon="mdi:chart-line" className="text-4xl mb-2" />
                            <p>No successful login data available</p>
                        </div>
                    </div>
                )}
            </div>

            {/* Failed Logins Bar Chart */}
            <div className="card-container p-4 rounded-md w-full flex flex-col gap-3">
                <div className="flex items-center gap-1">
                    <Icon icon="mdi:login" className="text-xl text-red-500" />
                    <h4 className="font-semibold text-font-primary">Failed Logins</h4>
                </div>
                
                {failedChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300} className="-ml-8">
                        <BarChart data={failedChartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.2} />
                            <XAxis 
                                dataKey="formattedDate" 
                                stroke="#9CA3AF"
                                fontSize={12}
                            />
                            <YAxis 
                                stroke="#9CA3AF"
                                fontSize={12}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar 
                                dataKey="count" 
                                fill="#EF4444"
                                radius={[2, 2, 0, 0]}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="flex items-center justify-center h-64 w-full text-font-secondary">
                        <div className="text-center">
                            <Icon icon="mdi:chart-bar" className="text-4xl mb-2" />
                            <p>No failed login data available</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LoginCharts;

